import { Component, Input, Output, EventEmitter, AfterViewInit, AfterContentInit, OnInit, OnDestroy, OnChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';


/**
 * Componente para especificar horas y minutos.
 * 
 * Muestra dos inputs grandes (horas y minutos) con:
 * - Botón superior para incrementar.
 * - Botón inferior para decrementar.
 *
 * @export
 * @class TimeInput
 * @implements {AfterViewInit}
 * @implements {AfterContentInit}
 * @implements {OnDestroy}
 * @implements {OnInit}
 * @implements {OnChanges}
 * @implements {ControlValueAccessor}
 */
@Component({
    selector: 'time-input',
    template: `
        <div>
            <ion-label text-center no-padding no-margin>{{title}}</ion-label>
            <div class="time">
                <div class="time-item">
                    <button ion-button icon-only
                        (click)="incHour()"
                    >
                        <ion-icon name="add"></ion-icon>
                    </button>
                    <ion-input type="number" 
                        [max]="24"
                        [min]="-1"
                        [(ngModel)]="sHour"
                        (ionChange)="hourChange($event)"
                    ></ion-input>
                    <button ion-button icon-only  color="secondary"
                        (click)="decHour()"
                    >
                        <ion-icon name="remove"></ion-icon>
                    </button> 
                </div>
                <div class="time-item">
                    <button ion-button icon-only
                        (click)="incMinute()"
                    >
                        <ion-icon name="add"></ion-icon>
                    </button>
                    <ion-input type="number" 
                        [max]="60"
                        [min]="-1"
                        [(ngModel)]="sMinute"
                         (ionChange)="minuteChange($event)"
                        >
                    </ion-input>
                    <button ion-button icon-only  color="secondary"
                        (click)="decMinute()"
                    >
                        <ion-icon name="remove"></ion-icon>
                    </button> 
                </div>
            </div>
        </div>

    `,
    providers: [
        { 
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TimeInput),
            multi: true
        }
    ],
    styles: [`
        .no-spin input[type=number]::-webkit-inner-spin-button, 
        .no-spin input[type=number]::-webkit-outer-spin-button { 
            -webkit-appearance: none; 
            margin: 0; 
        }

        input[type=number] {
            -moz-appearance:textfield;
        }   

        .time{
            display: flex;
        }
        
        .time-item{
            flex-direction: column;
            flex-basis: 50%;
            display: flex;
        }

        ion-input input{
            font-size: 24pt;
            text-align: center;
        }



        [inner-flex] ion-label{
            display: flex;
            flex-wrap: wrap;
            align-self: center;
        }

        .time-input{
            border:1px solid red;
        }
    `]
})

export class TimeInput implements AfterViewInit, AfterContentInit, OnDestroy, OnInit, OnChanges, ControlValueAccessor {

    /**
     * Propaga el cambio al modelo.
     *
     * @type {*}
     * @memberof TimeInput
     */
    propagateChange:any = () => {};

    /**
     * Implementa la funcion onTouch del modelo de datos.
     *
     * @memberof TimeInput
     */
    onTouchedCallback = () => {};

    /**
     * Implementa la función de validación del modelo de datos.
     *
     * @type {*}
     * @memberof TimeInput
     */
    validateFn:any = () => {};

    /**
     * EventEmitter de cambio.
     *
     * @type {EventEmitter<any>}
     * @memberof TimeInput
     */
    @Output() onChange : EventEmitter<any> = new EventEmitter();

    /**
     * Título del componente.
     *
     * @memberof TimeInput
     */
    @Input('title') title = '';

    /**
     * Devuelve el valor de incremento/decremento.
     *
     * @type {number}
     * @memberof TimeInput
     */
    get delta(): number {
        return this._delta;
    }
    /**
     * Input del valor de incremento/decremento
     *
     * @memberof TimeInput
     */
    @Input('delta') 
    set delta(delta: number) {
        this._delta = delta;
    }



    /**
     * Contiene el valor interno del componente.
     *
     * @memberof TimeInput
     */
    _value='00:00';

    /**
     * Contiene las horas del componente.
     *
     * @type {number}
     * @memberof TimeInput
     */
    _hour: number = 0;

    /**
     * Contiene los minutos del componente.
     *
     * @type {number}
     * @memberof TimeInput
     */
    _minute: number = 0;

    /**
     * Contiene el valor de horas del componente en formato texto.
     *
     * @type {string}
     * @memberof TimeInput
     */
    _sHour: string = '00';

    /**
     * Contiene el valor de los minutos del componente en formato texto.
     *
     * @type {string}
     * @memberof TimeInput
     */
    _sMinute: string = '00';

    /**
     * El valor a incrementar/decrementar con los botones +/-
     *
     * @type {number}
     * @memberof TimeInput
     */
    _delta: number = 5;


    /**
     * Crea una instancia de TimeInput.
     * 
     * @memberof TimeInput
     */
    constructor(){

    }


    /**
     * Valor getter.
     *
     * @readonly
     * @memberof TimeInput
     */
    @Input('value') 
    get value() {
        return this._value;
    }
  
    /**
     * Valor setter.
     *
     * @memberof TimeInput
     */
    set value(val) {
        //Break down time
        let timeRegExpr = /^([01]?[0-9]|2[0-3]|[0-9]):([0-5][0-9]|[0-9])(:[0-5][0-9])?/g
        if(!timeRegExpr.test(val)){
            val = '00:00';
        }
        let aVal = val.split(':');
        this.hour = parseInt(aVal[0]);
        this.minute = parseInt(aVal[1]);

        this._value = val;
        this.propagateChange(val);
        
    }

    /**
     * Horas getter.
     *
     * @memberof TimeInput
     */
    get hour(){
        return this._hour;
    }

    /**
     * Horas setter.
     *
     * @memberof TimeInput
     */
    set hour(hour: number){
        if(hour > 23) hour = 0;
        if(hour < 0) hour = 23;
        this._hour = hour;
        this._sHour = this.getTimeString(hour);
        this.applyTime();
    }

    /**
     * Minutos getter.
     *
     * @memberof TimeInput
     */
    get minute(){
        return this._minute;
    }

    /**
     * Minutos setter.
     *
     * @memberof TimeInput
     */
    set minute(minute: number){
        if(minute > 59) minute = 0;
        if(minute < 0) minute = 59;
        this._minute = minute;
        this._sMinute = this.getTimeString(minute);
        this.applyTime();
    }

    /**
     * Horas setter en texto. 
     *
     * @memberof TimeInput
     */
    get sHour(){
        return this._sHour;
    }

    /**
     *  Horas setter en texto.
     *
     * @memberof TimeInput
     */
    set sHour(sHour: string){
        this._sHour = sHour;
    }

    /**
     * Minutos getter en texto.
     *
     * @memberof TimeInput
     */
    get sMinute(){
        return this._sMinute;
    }

    /**
     * Minutos setter en texto.
     *
     * @memberof TimeInput
     */
    set sMinute(sMinute: string){
        this._sMinute = sMinute;
    }

    /**
     * Hook de destruir componente.
     * @memberof TimeInput
     */
    ngOnDestroy(){}

    /**
     * Hook de inicio de componente.
     *
     * @memberof TimeInput
     */
    ngOnInit(){}

    /**
     * Hook en cambio del componente.
     *
     * @memberof TimeInput
     */
    ngOnChanges(){

    }

    /**
     * Hook al iniciar contenido del componente.
     *
     * @memberof TimeInput
     */
    ngAfterContentInit(){}

    /**
     * Hook al inicio de vista del componente.
     *
     * @memberof TimeInput
     */
    ngAfterViewInit(){}

    /**
     * Establece el valor de componente.
     *
     * @param {*} value
     * @memberof TimeInput
     */
    writeValue(value) {
        if (value) {
            this.value = value;
        }
    }

    /**
     * Propaga los cambios del componente.
     *
     * @param {*} fn
     * @memberof TimeInput
     */
    registerOnChange(fn) {
        this.propagateChange = fn;
    }

    /**
     * Propaga si el componente és seleccionado.
     *
     * @param {*} fn
     * @memberof TimeInput
     */
    registerOnTouched(fn) {
        this.onTouchedCallback = fn;
    }

    /**
     * Formatea un número como texto.
     *
     * @param {number} [time]
     * @returns {string} El numero a formatear.
     * @memberof TimeInput
     */
    getTimeString(time?: number): string{
        return (time>9? '': '0') + time.toString();
    }

    /**
     * Aplica el tiempo y lo propaga. 
     *
     * @memberof TimeInput
     */
    applyTime(){
        let sHour = (this.hour>9? '': '0') + this.hour.toString();
        let sMinutes = (this.minute>9? '': '0') + this.minute.toString();
        this._value = sHour + ':' + sMinutes;
        this.propagateChange(this._value);
    }

    /**
     * Incremente las horas en 1.
     *
     * @memberof TimeInput
     */
    incHour(){
        let h = (this.hour + 1)%24; 
        this.hour = h;
    }

    /**
     * Decrementa las horas en 1.
     *
     * @memberof TimeInput
     */
    decHour(){
        let h = (this.hour - 1)%24; 
        this.hour = h;
    }

   /**
     * Incrementa los minutos en delta.
     *
     * @memberof TimeInput
     */
    incMinute(){
        let m = (this.minute + this.delta)%60; 
        this.minute = m;
        //this.modInput(this.minute, 59);
    }

    /**
     * Decrementa los minutos en delta.
     *
     * @memberof TimeInput
     */
    decMinute(){
        let m = (this.minute - this.delta)%60; 
        this.minute = m;
        //this.modInput(this.minute, 59);
    }

    /**
     * Realiza el módulo de un input.
     *
     * @param {*} [input=this.minute]
     * @param {number} [mod=59]
     * @memberof TimeInput
     */
    modInput(input: any = this.minute, mod = 59){
        input = input % mod;
    }


    minuteChange(){ 
        setTimeout(()=>{
            if(this.sMinute.length>2){
                let m = this.sMinute.substring(this.sMinute.length-2);
                this.minute = parseInt(this.sMinute);
                
                    this.sMinute = m; 
                
            }
            else if(this.getTimeString(this.minute) != this.sMinute){
                this.minute = parseInt(this.sMinute);
            }
        });
    }

    hourChange(){
        setTimeout(()=>{
            if(this.sHour.length>2){
                let h = this.sHour.substring(this.sHour.length-2);
                this.hour = parseInt(this.sHour);
                this.sHour = h;   
            }
            else if(this.getTimeString(this.hour) != this.sHour){
                this.hour = parseInt(this.sHour);
            }
        });
    }
}


// WEBPACK FOOTER //
// ./src/modules/utils/time-input/time-input.component.ts